import { GoogleButton, OrSeparator } from "@/components/auth/GoogleButton";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Lien d'invitation invalide");
      navigate("/login", { replace: true });
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Mot de passe trop court (min 6 caractères)");
      return;
    }
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.acceptOrgInvite({
        token,
        password,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
      });
      // L'invitation contient l'email — le user vient d'être créé.
      // On déduit l'email via /orgs/me après login. Ici on demande à l'utilisateur de se connecter.
      setDone(true);
      toast.success("Invitation acceptée — connectez-vous pour accéder à l'organisation.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(getErrorMessage(err, "Acceptation impossible"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-background to-amber-50 p-4">
      <Card className="w-full max-w-md shadow-xl" data-testid="accept-invite-card">
        <CardHeader className="space-y-2 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading">Rejoindre l'organisation</CardTitle>
          <CardDescription>
            Créez votre compte Bati Assistant pour accepter cette invitation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
              <p className="font-medium">Invitation acceptée !</p>
              <p className="text-sm text-muted-foreground">Redirection vers la page de connexion…</p>
            </div>
          ) : (
            <div className="space-y-4">
            <GoogleButton returnPath={`/accept-invite?token=${encodeURIComponent(token || "")}`}
              label="Accepter avec Google" testId="invite-google-btn" />
            <OrSeparator />
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="first_name">Prénom</Label>
                  <Input
                    id="first_name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    data-testid="invite-first-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="last_name">Nom</Label>
                  <Input
                    id="last_name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    data-testid="invite-last-name"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Mot de passe (min. 6)</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="invite-password"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirmer</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="invite-password-confirm"
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-accept-invite">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Rejoindre l'organisation
              </Button>
            </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
